import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { AdminRole } from "@upclaim/sdk/src/extra-types";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { AdminUser } from "../lib/users-grouping";

/** GET /admin/admins — the SDK types the response as object[], narrow it here. */
export function useAdmins() {
  return useQuery({
    queryKey: queryKeys.admins.list(),
    queryFn: async () => (await adminAPI.listAdmin()) as AdminUser[],
  });
}

/**
 * GET /admin/teams — options for the create-admin modal's Équipe select. `tenantId`
 * (Upclaim only) lists another tenant's teams instead of the caller's own.
 */
export function useTeams(tenantId?: string) {
  return useQuery({
    queryKey: queryKeys.admins.teams(tenantId),
    queryFn: () => adminAPI.getTeams(tenantId ? { tenantId } : undefined),
  });
}

/**
 * POST /admin/teams — inline team creation, scoped to the selected tenant. `members` crée
 * l'équipe ET y rattache les comptes en un appel (le back appelle `setAdminsTeam`).
 */
export function useCreateTeam(tenantId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, members }: { name: string; members?: string[] }) =>
      notify.promise(adminAPI.postTeam({ name, members, tenantId }), {
        loading: "Création de l'équipe…",
        success: `Équipe "${name}" créée`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.teams(tenantId) });
    },
  });
}

/** POST /admin/admins/assign-team — affectation de lot depuis l'îlot de sélection. */
export function useAssignAdminsTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      adminIds,
      teamName,
      teamId,
    }: {
      adminIds: string[];
      teamId: string | null;
      teamName: string;
    }) =>
      notify.promise(adminAPI.assignAdminsTeam({ adminIds, teamId }), {
        loading: "Affectation à l'équipe…",
        success: teamId
          ? `${adminIds.length > 1 ? `${adminIds.length} utilisateurs affectés` : "Utilisateur affecté"} à « ${teamName} »`
          : `${adminIds.length > 1 ? `${adminIds.length} utilisateurs retirés` : "Utilisateur retiré"} de leur équipe`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.all });
    },
  });
}

export interface CreateAdminFormInput {
  email: string;
  firstName: string;
  lastName: string;
  role: "CUSTOMER_AGENT" | "CUSTOMER_ACCOUNT_MANAGER" | "CUSTOMER_ADMIN";
  teamId?: string;
  gdsSignature?: string;
  /** Point de vente GDS de l'agent — n'a d'effet qu'avec une signature. */
  travelAgencyOfficeId?: number;
  /** Tenant the user is created in — Upclaim direction only, defaults server-side to the caller's tenant. */
  tenantId?: string;
}

export interface CreateAdminsInput {
  users: CreateAdminFormInput[];
  inviteImmediately: boolean;
}

/**
 * Port of Users.vue createAdmin — POST /admin/admins with an ARRAY payload (the whole
 * batch goes in one call), then refetch the list to resolve the new admins' ids by EMAIL
 * (createAdmin only returns { success }) and optionally invite them right away.
 */
export function useCreateAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAdminsInput) =>
      notify.promise(
        (async () => {
          const { success } = await adminAPI.createAdmin(input.users);
          if (!success) {
            throw new Error("La création des utilisateurs a échoué");
          }

          if (input.inviteImmediately) {
            const admins = (await adminAPI.listAdmin()) as AdminUser[];
            const createdIds = input.users
              .map((user) => admins.find((admin) => admin.email === user.email)?.id)
              .filter((adminId): adminId is string => Boolean(adminId));
            if (createdIds.length) {
              await adminAPI.inviteAdmin({ adminsIds: createdIds });
            }
          }
          return input;
        })(),
        {
          loading: input.users.length > 1 ? "Création des utilisateurs…" : "Création de l'utilisateur…",
          success: ({ users, inviteImmediately }) => ({
            message:
              users.length > 1
                ? `${users.length} utilisateurs ajoutés avec succès`
                : `Utilisateur "${users[0].firstName} ${users[0].lastName?.toUpperCase() ?? ""}" ajouté avec succès`,
            description: inviteImmediately
              ? "Un email d'invitation vient de leur être envoyé"
              : "Merci d'inviter les utilisateurs pour activer leur compte",
          }),
        },
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.all });
    },
  });
}

/** POST /admin/admins/invite — bulk invite from the selection toolbar. */
export function useInviteAdmins() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (adminsIds: string[]) =>
      notify.promise(adminAPI.inviteAdmin({ adminsIds }), {
        loading: "Envoi des invitations…",
        success:
          adminsIds.length === 1
            ? "Utilisateur invité avec succès"
            : "Les utilisateurs ont été invités avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.all });
    },
  });
}

/** POST /admin/admins/access — désactivation ou réactivation de lot depuis l'îlot de sélection. */
export function useSetAdminsAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adminIds, isActive }: { adminIds: string[]; isActive: boolean }) => {
      const subject = adminIds.length > 1 ? `${adminIds.length} utilisateurs` : "Utilisateur";
      const verb = isActive ? "réactivé" : "désactivé";
      return notify.promise(adminAPI.setAdminsAccess({ adminIds, isActive }), {
        loading: isActive ? "Réactivation de l'accès…" : "Désactivation de l'accès…",
        success: `${subject} ${verb}${adminIds.length > 1 ? "s" : ""}`,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.all });
    },
  });
}

export type UpdateAdminInput = Parameters<typeof adminAPI.updateAdmin>[1];

/** PATCH /admin/admins/:adminId — edit an account from the Users page. */
export function useUpdateAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ adminId, ...changes }: { adminId: string } & UpdateAdminInput) =>
      notify.promise(adminAPI.updateAdmin(adminId, changes), {
        loading: "Mise à jour de l'utilisateur…",
        success: `Utilisateur "${changes.fullName}" mis à jour`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.all });
    },
  });
}

/**
 * Batch role change from the selection island. There is no batch endpoint: each account
 * goes through PATCH /admin/admins/:adminId with its current fields and the new role.
 */
export function useAssignAdminsRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ admins, role, roleLabel }: { admins: AdminUser[]; role: AdminRole; roleLabel: string }) =>
      notify.promise(
        Promise.all(
          admins.map((admin) =>
            adminAPI.updateAdmin(admin.id, {
              fullName: admin.fullName,
              email: admin.email ?? "",
              role,
              teamId: admin.team?.id ?? null,
              gdsSignature: admin.travelAgencyAgent?.gdsSignature ?? null,
              travelAgencyOfficeId: admin.travelAgencyAgent?.office?.id ?? null,
            }),
          ),
        ),
        {
          loading: "Changement de rôle…",
          success: `${admins.length > 1 ? `${admins.length} utilisateurs passés` : "Utilisateur passé"} « ${roleLabel} »`,
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admins.all });
    },
  });
}
